/**
 * Backups taken on this device before a map's stored format changes
 * (IndexedDB `orbital-migration-backups`, store `backups`): before a
 * conversion to schema v2 (engine/sync/map-format-migration.js, dropped once
 * done) and before its old copy is dropped
 * (engine/sync/old-copy-drop.js, docs/plans/2026-10-13-drop-schema-v1.md).
 *
 * An old-copy backup is the whole map before the drop, at
 * `<docId>:old-copy`; a small index record lists them with their time and
 * whether the compaction that followed is confirmed, so listing never reads
 * the maps. Kept 30 days, and until that compaction is confirmed; the
 * account's online maps' backups go at sign-out. Like the device's copy of a
 * map, not encrypted on the device (encryption.md "Data on the device").
 */

const BACKUP_DB = "orbital-migration-backups";
const OLD_COPY_SUFFIX = ":old-copy";
const OLD_COPY_INDEX = "old-copy-index";
const KEEP_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * One transaction on the backups store; resolves once it is committed
 * (false where there is no IndexedDB).
 * @param {IDBTransactionMode} mode
 * @param {(store: IDBObjectStore) => void} work
 */
export function backupStore(mode, work) {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return resolve(false);
    const request = indexedDB.open(BACKUP_DB, 1);
    request.onupgradeneeded = (event) => event.target.result.createObjectStore("backups");
    request.onerror = () => reject(request.error);
    request.onsuccess = (event) => {
      const db = event.target.result;
      const tx = db.transaction("backups", mode);
      work(tx.objectStore("backups"));
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror = () => { db.close(); reject(tx.error); };
      tx.onabort = () => { db.close(); reject(tx.error || new Error("Backup transaction aborted")); };
    };
  });
}

/** Read the index, change it with `edit(index, store)`, write it back: one transaction. */
function editIndex(edit) {
  return backupStore("readwrite", (store) => {
    const read = store.get(OLD_COPY_INDEX);
    read.onsuccess = () => {
      const index = read.result && typeof read.result === "object" ? read.result : {};
      edit(index, store);
      store.put(index, OLD_COPY_INDEX);
    };
  });
}

/**
 * Keep the map as it is before its old copy is dropped.
 * @returns {Promise<boolean>} true once it is committed (no backup, no drop)
 */
export async function saveOldCopyBackup(docId, bytes, at = Date.now()) {
  if (!docId || !bytes?.length) return false;
  try {
    return await editIndex((index, store) => {
      store.put(bytes, docId + OLD_COPY_SUFFIX);
      index[docId] = { at, compacted: false };
    });
  } catch (err) {
    console.warn("[Backups] Could not save the backup before dropping the old copy of", docId, err);
    return false;
  }
}

/** The compaction after this map's drop is confirmed: its backup may now expire. */
export async function markOldCopyCompacted(docId) {
  try {
    await editIndex((index) => { if (index[docId]) index[docId].compacted = true; });
  } catch (err) {
    console.warn("[Backups] Could not note the compaction of", docId, err);
  }
}

/** The old-copy backups: docId → {at, compacted}. */
export async function listOldCopyBackups() {
  let index = {};
  try {
    await backupStore("readonly", (store) => {
      const read = store.get(OLD_COPY_INDEX);
      read.onsuccess = () => { if (read.result && typeof read.result === "object") index = read.result; };
    });
  } catch (err) {
    console.warn("[Backups] Could not list the old-copy backups:", err);
  }
  return index;
}

/** The map as it was before its old copy was dropped, or null. */
export async function readOldCopyBackup(docId) {
  let bytes = null;
  await backupStore("readonly", (store) => {
    const read = store.get(docId + OLD_COPY_SUFFIX);
    read.onsuccess = () => { bytes = read.result || null; };
  });
  return bytes;
}

/** Remove these maps' old-copy backups (a refused drop, sign-out). */
export async function forgetOldCopyBackups(docIds) {
  const ids = (docIds || []).filter((id) => typeof id === "string" && id);
  if (!ids.length) return;
  try {
    await editIndex((index, store) => {
      for (const id of ids) {
        store.delete(id + OLD_COPY_SUFFIX);
        delete index[id];
      }
    });
  } catch (err) {
    console.warn("[Backups] Could not remove old-copy backups:", err);
  }
}

/** Remove the backups older than 30 days whose compaction is confirmed. */
export async function pruneOldCopyBackups(now = Date.now()) {
  try {
    await editIndex((index, store) => {
      for (const [id, entry] of Object.entries(index)) {
        if (!entry?.compacted || now - entry.at < KEEP_MS) continue;
        store.delete(id + OLD_COPY_SUFFIX);
        delete index[id];
      }
    });
  } catch (err) {
    console.warn("[Backups] Could not remove expired old-copy backups:", err);
  }
}
